import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { APPLIED_FILTER_CHIP_GAP } from '@constants/appliedFilterChips';
import useResizeObserver from '@hooks/useResizeObserver';
import { ChipRowLayout, fitChips } from '@utils/chipRowFit';

/**
 * Decides how many of `chipCount` chips fit in the row and how many collapse
 * into +N.
 *
 * The caller renders every chip twice: once in a hidden, unconstrained
 * measuring row (all chips, then one +N probe as the last child) and once in
 * the visible row, sliced by the returned counts. Widths are read from the
 * measuring row rather than cached, because a chip can be hidden from the
 * moment it first appears and must still be measurable for the next widen.
 * Recomputed before paint after every render in which `chipCount` or
 * `measureKey` changed, and on every container resize. `measureKey` is never
 * read: it is in the dependency list only so a text change re-creates the
 * recompute and re-runs the layout pass.
 */
const useChipRowOverflow = (chipCount: number, measureKey: string) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRowRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ChipRowLayout>({
    visibleCount: chipCount,
    hiddenCount: 0,
  });

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measureRow = measureRowRef.current;

    if (!container || !measureRow) {
      return;
    }

    const probes = Array.from(measureRow.children) as HTMLElement[];
    const chipWidths = probes
      .slice(0, chipCount)
      .map((probe) => probe.offsetWidth);
    const overflowChipWidth = probes[chipCount]?.offsetWidth ?? 0;

    const next = fitChips(
      chipWidths,
      container.clientWidth,
      APPLIED_FILTER_CHIP_GAP,
      overflowChipWidth,
    );

    setLayout((current) =>
      current.visibleCount === next.visibleCount &&
      current.hiddenCount === next.hiddenCount
        ? current
        : next,
    );
  }, [chipCount, measureKey]);

  useLayoutEffect(recompute, [recompute]);
  useResizeObserver(containerRef, recompute);

  return { containerRef, measureRowRef, ...layout };
};

export default useChipRowOverflow;
